import Modal from '@/src/Common/components/Modal';
import useShare from '@/src/Share/components/hooks/useShare';
import { Dialog } from '@headlessui/react';
import { PlusIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

export default function MergeSessionsModal({
    photoSessionId,
    sessions,
    onComplete,
}: {
    photoSessionId: string;
    sessions: any[];
    onComplete: () => void;
}) {
    const cancelButtonRef = useRef(null);
    const [isMergingSessions, setIsMergingSessions] = useState(false);
    const { mergeSessions } = useShare();
    const [sourcePhotoSessionIds, setSourcePhotoSessionIds] = useState<
        string[]
    >([]);

    const merge = async () => {
        setIsMergingSessions(true);
        await mergeSessions(photoSessionId, sourcePhotoSessionIds);
        setIsMergingSessions(false);
        toast.success('Sessions merged');
        onComplete();
    };

    return (
        <Modal cancelButtonRef={cancelButtonRef} onDismiss={onComplete}>
            <div>
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[99999px] bg-black sm:mx-0 sm:h-10 sm:w-10">
                            <PlusIcon
                                className="h-6 w-6 text-white"
                                aria-hidden="true"
                            />
                        </div>
                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                            <Dialog.Title
                                as="h3"
                                className="text-base font-semibold leading-6 text-gray-900"
                            >
                                Merge Sessions
                            </Dialog.Title>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500">
                                    Please select the sessions you want to
                                    merge.
                                    <br />
                                    <br /> Merging a total of{' '}
                                    {sourcePhotoSessionIds.length} sessions.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="ml-12 mt-2 p-2">
                    <div>
                        {sessions.map((session) => {
                            if (session.photoSessionId === photoSessionId) {
                                return null;
                            }

                            return (
                                <div key={session.photoSessionId}>
                                    <input
                                        type="checkbox"
                                        className="mr-2"
                                        id={session.photoSessionId}
                                        checked={sourcePhotoSessionIds.includes(
                                            session.photoSessionId,
                                        )}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSourcePhotoSessionIds([
                                                    ...sourcePhotoSessionIds,
                                                    session.photoSessionId,
                                                ]);
                                            } else {
                                                setSourcePhotoSessionIds(
                                                    sourcePhotoSessionIds.filter(
                                                        (id) =>
                                                            id !==
                                                            session.photoSessionId,
                                                    ),
                                                );
                                            }
                                        }}
                                    />
                                    <label htmlFor={session.photoSessionId}>
                                        {session.date} - {session.total}
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                    type="button"
                    className="mt-3 inline-flex w-full ml-4 justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => onComplete()}
                    ref={cancelButtonRef}
                >
                    Cancel
                </button>
                <br />
                <button
                    onClick={merge}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-black px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-gray-800 hover:bg-black-50 sm:mt-0 sm:w-auto"
                    disabled={isMergingSessions}
                >
                    {isMergingSessions ? 'Merging...' : 'Merge Sessions'}
                </button>
            </div>
        </Modal>
    );
}
